import { useState } from 'react';
import { Stack, Paper, TextInput, Group, Text, Skeleton, Tabs, Badge, SimpleGrid } from '@mantine/core';
import { IconSearch, IconTarget, IconChartBar } from '@tabler/icons-react';
import { DataTable, type DataTableSortStatus } from 'mantine-datatable';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  useGoogleAdsConversionActions,
  useGoogleAdsConversionStats,
  useGoogleAdsAccount,
} from '@/hooks/useGoogleAds';
import type { DateRangeOption, ConversionAction, ConversionStats } from '@/types/googleAds';
import { MetricCard } from '../components/MetricCard';
import { StatusBadge } from '../components/StatusBadge';

interface ConversionsTabProps {
  customerId: string;
  dateRange: DateRangeOption;
}

export function ConversionsTab({ customerId, dateRange }: ConversionsTabProps) {
  const { data: conversionActions, isLoading: actionsLoading } = useGoogleAdsConversionActions(customerId);
  const { data: conversionStats, isLoading: statsLoading } = useGoogleAdsConversionStats(
    customerId,
    dateRange,
  );
  const { data: account } = useGoogleAdsAccount(customerId);

  const [activeTab, setActiveTab] = useState<string | null>('actions');
  const [search, setSearch] = useState('');

  const [statsSort, setStatsSort] = useState<DataTableSortStatus<ConversionStats>>({
    columnAccessor: 'conversions',
    direction: 'desc',
  });

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: account?.currency ?? 'EUR' }).format(value);

  const formatNumber = (num: number) => new Intl.NumberFormat('de-DE').format(num);

  const isLoading = actionsLoading || statsLoading;

  const filteredActions = conversionActions?.filter((action) => {
    if (search && !action.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const filteredStats = conversionStats
    ?.filter((stat) => {
      if (search && !stat.conversionActionName.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      const accessor = statsSort.columnAccessor as keyof ConversionStats;
      const aVal = a[accessor] as number;
      const bVal = b[accessor] as number;
      return statsSort.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });

  const totalConversions = conversionStats?.reduce((sum, s) => sum + s.conversions, 0) ?? 0;
  const totalValue = conversionStats?.reduce((sum, s) => sum + s.conversionValue, 0) ?? 0;
  const enabledActions = conversionActions?.filter((a) => a.status === 'ENABLED').length ?? 0;

  const chartData = filteredStats?.slice(0, 10).map((stat) => ({
    name: stat.conversionActionName.length > 20
      ? stat.conversionActionName.substring(0, 20) + '...'
      : stat.conversionActionName,
    conversions: stat.conversions,
    value: stat.conversionValue,
  }));

  if (isLoading) {
    return (
      <Stack gap="md">
        <Skeleton height={40} />
        <Skeleton height={400} />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 2, md: 4 }}>
        <MetricCard
          title="Conversions gesamt"
          value={formatNumber(totalConversions)}
          icon={<IconTarget size={16} />}
          color="violet"
        />
        <MetricCard
          title="Conversion-Wert"
          value={formatCurrency(totalValue)}
          icon={<IconChartBar size={16} />}
          color="green"
        />
        <MetricCard
          title="Aktive Actions"
          value={enabledActions}
          subtitle={`von ${conversionActions?.length ?? 0}`}
          color="blue"
        />
        <MetricCard
          title="Durchschn. Wert"
          value={totalConversions > 0 ? formatCurrency(totalValue / totalConversions) : '-'}
          color="cyan"
        />
      </SimpleGrid>

      <Paper withBorder p="md">
        <Group>
          <TextInput
            placeholder="Conversion suchen..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={250}
          />
        </Group>
      </Paper>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="actions">
            Conversion Actions{' '}
            <Badge size="sm" variant="light" ml={4}>
              {conversionActions?.length ?? 0}
            </Badge>
          </Tabs.Tab>
          <Tabs.Tab value="stats">
            Statistiken{' '}
            <Badge size="sm" variant="light" ml={4}>
              {conversionStats?.length ?? 0}
            </Badge>
          </Tabs.Tab>
          <Tabs.Tab value="chart">Übersicht</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="actions" pt="md">
          <DataTable
            withTableBorder
            borderRadius="sm"
            striped
            highlightOnHover
            records={filteredActions ?? []}
            columns={[
              {
                accessor: 'name',
                title: 'Name',
                width: 250,
              },
              {
                accessor: 'category',
                title: 'Kategorie',
                width: 150,
                render: (action) => (
                  <Badge variant="light" size="sm">
                    {action.category.replace(/_/g, ' ')}
                  </Badge>
                ),
              },
              {
                accessor: 'type',
                title: 'Typ',
                width: 180,
                render: (action) => (
                  <Text size="xs" c="dimmed">
                    {action.type.replace(/_/g, ' ')}
                  </Text>
                ),
              },
              {
                accessor: 'status',
                title: 'Status',
                width: 100,
                render: (action) => <StatusBadge status={action.status} />,
              },
              {
                accessor: 'countingType',
                title: 'Zählung',
                width: 130,
                render: (action) => (
                  <Badge variant="outline" size="sm">
                    {action.countingType === 'ONE_PER_CLICK' ? 'Eine pro Klick' : 'Alle'}
                  </Badge>
                ),
              },
              {
                accessor: 'attributionModel',
                title: 'Attribution',
                width: 150,
              },
              {
                accessor: 'conversionWindow',
                title: 'Fenster',
                width: 80,
                textAlign: 'right',
                render: (action) => `${action.conversionWindow} Tage`,
              },
              {
                accessor: 'includeInConversionsMetric',
                title: 'In Conversions',
                width: 110,
                render: (action) => (
                  <Badge color={action.includeInConversionsMetric ? 'green' : 'gray'} variant="light" size="sm">
                    {action.includeInConversionsMetric ? 'Ja' : 'Nein'}
                  </Badge>
                ),
              },
              {
                accessor: 'valueSetting.defaultValue',
                title: 'Standardwert',
                width: 120,
                textAlign: 'right',
                render: (action) =>
                  action.valueSetting?.defaultValue ? formatCurrency(action.valueSetting.defaultValue) : '-',
              },
            ]}
            noRecordsText="Keine Conversion Actions gefunden"
            minHeight={400}
          />
        </Tabs.Panel>

        <Tabs.Panel value="stats" pt="md">
          <DataTable
            withTableBorder
            borderRadius="sm"
            striped
            highlightOnHover
            records={filteredStats ?? []}
            sortStatus={statsSort}
            onSortStatusChange={setStatsSort}
            columns={[
              {
                accessor: 'conversionActionName',
                title: 'Conversion Action',
                width: 300,
              },
              {
                accessor: 'conversions',
                title: 'Conversions',
                sortable: true,
                textAlign: 'right',
                width: 120,
                render: (stat) => formatNumber(stat.conversions),
              },
              {
                accessor: 'conversionValue',
                title: 'Wert',
                sortable: true,
                textAlign: 'right',
                width: 120,
                render: (stat) => formatCurrency(stat.conversionValue),
              },
              {
                accessor: 'allConversions',
                title: 'Alle Conv.',
                sortable: true,
                textAlign: 'right',
                width: 110,
                render: (stat) => formatNumber(stat.allConversions),
              },
              {
                accessor: 'allConversionValue',
                title: 'Alle Wert',
                sortable: true,
                textAlign: 'right',
                width: 120,
                render: (stat) => formatCurrency(stat.allConversionValue),
              },
              {
                accessor: 'viewThroughConversions',
                title: 'View-Through',
                sortable: true,
                textAlign: 'right',
                width: 110,
                render: (stat) => formatNumber(stat.viewThroughConversions),
              },
              {
                accessor: 'dateRange',
                title: 'Zeitraum',
                width: 180,
                render: (stat) =>
                  `${new Date(stat.dateRange.startDate).toLocaleDateString('de-DE')} - ${new Date(stat.dateRange.endDate).toLocaleDateString('de-DE')}`,
              },
            ]}
            noRecordsText="Keine Statistiken gefunden"
            minHeight={400}
          />
        </Tabs.Panel>

        <Tabs.Panel value="chart" pt="md">
          <Paper withBorder p="md">
            <Text fw={500} mb="md">
              Top 10 Conversion Actions
            </Text>
            {chartData && chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={chartData} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value: number, name: string) =>
                      name === 'conversions'
                        ? [formatNumber(value), 'Conversions']
                        : [formatCurrency(value), 'Wert']
                    }
                  />
                  <Bar dataKey="conversions" fill="#7950f2" name="conversions" />
                  <Bar dataKey="value" fill="#40c057" name="value" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Text c="dimmed" ta="center" py="xl">
                Keine Daten verfügbar
              </Text>
            )}
          </Paper>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
